// src/utils/folderMirror.ts
//
// Scan a local folder (drag-and-drop or input picker) and compare against
// a SharePoint tree. Produces a diff of folders to create and files to upload.
//
// Uses FileSystemEntry (webkitGetAsEntry) to walk drag-and-drop structures
// so we catch both files AND directory entries, including empty folders.

import JSZip from "jszip";
import { unzip } from "fflate";
import type { SPTreeNode } from "./sharePointTree";
import { flattenTreeByPath, flattenTreeFilePaths } from "./sharePointTree";
import { normalizeZipForJSZip } from "./normalizeZipForJSZip";

// ---------- Local scan result ----------

export type LocalScan = {
  files: File[];          // each file has a `__mirrorPath` property with its relative path
  folderPaths: string[];  // every folder path encountered (relative, no wrapper)
};

/**
 * Scan a DataTransferItemList (drop) or FileList (input picker).
 * Returns all files AND all folder paths (including empty folders, for drops).
 *
 * The "wrapper" — the top-level folder the user dragged — is stripped from paths.
 * Example: drop "my-folder" containing "A/file.pdf" →
 *   files[0].__mirrorPath = "A/file.pdf"
 *   folderPaths = ["A"]
 */
export async function scanLocalTree(
  items: DataTransferItemList | FileList | DroppedEntries,
  excluded: Set<string> = new Set(),
  onProgress?: (files: number, folders: number) => void
): Promise<LocalScan> {
  const ctx: WalkCtx = { files: [], folderPaths: new Set(), excluded, onProgress };
  const { files, folderPaths } = ctx;

  if ((items as DroppedEntries).kind === "entries") {
    // Entries captured earlier by captureDropEntries (lazy exclude flow)
    for (const entry of (items as DroppedEntries).roots) {
      await walkEntry(entry, "", ctx);
    }
  } else if ((items as any)[0]?.webkitGetAsEntry) {
    const dropped = await captureDropEntries(items as DataTransferItemList);
    for (const entry of dropped.roots) {
      await walkEntry(entry, "", ctx);
    }
  } else {
    // Input-picker FileList — webkitRelativePath already has wrapper/sub/file format
    for (const file of items as any as File[]) {
      const rel = (file as any).webkitRelativePath || file.name;
      const parts = rel.split("/").filter(Boolean);

      // Strip first segment (wrapper)
      const pathWithoutWrapper = parts.slice(1).join("/");
      if (!pathWithoutWrapper) continue;

      const segs = pathWithoutWrapper.split("/");
      const folderPath = segs.slice(0, -1).join("/");
      if (folderPath && isUnderExcluded(folderPath, excluded)) continue;

      (file as any).__mirrorPath = pathWithoutWrapper;
      files.push(file);

      // Derive folder paths from the file's path
      for (let i = 1; i < segs.length; i++) {
        folderPaths.add(segs.slice(0, i).join("/"));
      }
    }
  }

  const folderArr = Array.from(folderPaths).sort();
  console.log(`📂 scanLocalTree: ${files.length} files, ${folderArr.length} folders`);
  if (folderArr.length > 0) console.log(`   Folders:`, folderArr.slice(0, 20));
  if (files.length > 0) {
    const samplePaths = files.slice(0, 5).map((f) => (f as any).__mirrorPath);
    console.log(`   Sample file paths:`, samplePaths);
  }

  return { files, folderPaths: folderArr };
}

// ---------- Drop capture (lazy exclude flow) ----------

export type DroppedEntries = {
  kind: "entries";
  roots: any[]; // FileSystemEntry[] to walk from path "" (wrapper already stripped)
};

/**
 * Snapshot the FileSystemEntries from a drop. Must be called synchronously
 * inside the drop handler (the DataTransferItemList is only readable then).
 * Only the wrapper folder (if any) is listed — nothing below it is touched,
 * so this stays instant even on cloud-backed drives like Box.
 */
export function captureDropEntries(items: DataTransferItemList): Promise<DroppedEntries> {
  const topEntries: any[] = [];
  for (let i = 0; i < items.length; i++) {
    const entry = (items as any)[i]?.webkitGetAsEntry?.();
    if (entry) topEntries.push(entry);
  }
  return (async () => {
    // If user dragged a single folder, treat it as the wrapper (strip its name).
    // If user dragged multiple folders/files, don't strip — each is a target.
    const singleWrapper = topEntries.length === 1 && topEntries[0].isDirectory;
    const roots = singleWrapper
      ? await readAllEntries(topEntries[0].createReader())
      : topEntries;
    return { kind: "entries", roots };
  })();
}

/** List only the direct subfolders of a directory entry (one level, not recursive). */
export async function listSubfolders(dirEntry: any): Promise<any[]> {
  const all = await readAllEntries(dirEntry.createReader());
  return all
    .filter((e) => e.isDirectory)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
}

// ---------- Recursive walker for FileSystemEntry ----------

type WalkCtx = {
  files: File[];
  folderPaths: Set<string>;
  excluded: Set<string>; // lowercase folder paths to skip entirely
  onProgress?: (files: number, folders: number) => void;
};

async function walkEntry(entry: any, pathPrefix: string, ctx: WalkCtx): Promise<void> {
  const myPath = pathPrefix ? `${pathPrefix}/${entry.name}` : entry.name;

  if (entry.isFile) {
    await new Promise<void>((resolve) => {
      entry.file(
        (file: File) => {
          (file as any).__mirrorPath = myPath;
          ctx.files.push(file);
          if (ctx.files.length % 50 === 0) ctx.onProgress?.(ctx.files.length, ctx.folderPaths.size);
          resolve();
        },
        () => resolve()
      );
    });
    return;
  }

  if (entry.isDirectory) {
    // Excluded folders are never listed — this is what keeps Box Drive quiet.
    if (isUnderExcluded(myPath, ctx.excluded)) return;
    ctx.folderPaths.add(myPath);
    ctx.onProgress?.(ctx.files.length, ctx.folderPaths.size);
    const children = await readAllEntries(entry.createReader());
    for (const child of children) {
      await walkEntry(child, myPath, ctx);
    }
  }
}

// readEntries() returns at most ~100 at a time — call until empty
async function readAllEntries(reader: any): Promise<any[]> {
  const all: any[] = [];
  while (true) {
    const batch = await new Promise<any[]>((resolve) => {
      reader.readEntries(resolve, () => resolve([]));
    });
    if (!batch || batch.length === 0) break;
    all.push(...batch);
  }
  return all;
}

// ---------- Cloud-drive placeholder hydration ----------

/**
 * Read a file fully into memory so it can be uploaded.
 *
 * Files dragged from cloud drives that stream on demand (Box Drive especially;
 * OneDrive Files On-Demand behaves the same way) are placeholders — the bytes
 * are not on disk until something reads them. Handing such a File straight to
 * XHR fails: the file changes underneath the browser as Box fetches it, so the
 * request is aborted with net::ERR_FAILED, which axios surfaces as the useless
 * "Network Error". Dropbox keeps real files on disk, which is why it works.
 *
 * Reading the bytes here forces Box to hydrate the file and detaches the upload
 * from the file on disk, so the request body can no longer change mid-flight.
 */
export async function materializeFile(file: File): Promise<File> {
  const buf = await file.arrayBuffer();
  if (buf.byteLength !== file.size) {
    throw new Error(
      `Read ${buf.byteLength} of ${file.size} bytes — the file may still be downloading from the cloud drive.`
    );
  }
  const copy = new File([buf], file.name, { type: file.type, lastModified: file.lastModified });
  (copy as any).__mirrorPath = (file as any).__mirrorPath;
  return copy;
}

// ---------- ZIP expansion for mirroring ----------

/**
 * Extract a ZIP file into a LocalScan, preserving the full folder structure.
 * Strips the top-level wrapper folder (same as scanLocalTree does for drops).
 * Uses JSZip first, fflate as fallback (same as expandZipFiles).
 */
export async function expandZipForMirror(zipFile: File): Promise<LocalScan> {
  // Intralinks archives carry a ZIP64 trailer that JSZip misreads as
  // prepended data; strip it (zero-copy) when present, no-op otherwise.
  zipFile = await normalizeZipForJSZip(zipFile);

  const files: File[] = [];
  const folderPaths = new Set<string>();

  console.log(`📦 Expanding ZIP for mirror: ${zipFile.name}`);

  let entries: { path: string; blob: Blob }[] = [];

  // Try JSZip first, then fflate
  try {
    const zip = await JSZip.loadAsync(zipFile);
    const jsEntries = Object.entries(zip.files);
    console.log(`   Found ${jsEntries.length} entries via JSZip`);

    for (const [path, entry] of jsEntries) {
      if (entry.dir) continue;
      const blob = await entry.async("blob");
      entries.push({ path, blob });
    }
  } catch (err) {
    console.warn(`   ⚠️ JSZip failed, trying fflate...`, err);
    try {
      const buffer = await zipFile.arrayBuffer();
      const data = await new Promise<Record<string, Uint8Array>>((resolve, reject) => {
        unzip(new Uint8Array(buffer), (e, d) => (e ? reject(e) : resolve(d)));
      });
      for (const [path, content] of Object.entries(data)) {
        if (path.endsWith("/") || content.length === 0) continue;
        entries.push({ path, blob: new Blob([content.buffer as ArrayBuffer]) });
      }
      console.log(`   ✅ fflate extracted ${entries.length} file entries`);
    } catch (fallbackErr) {
      console.error("   ❌ Both JSZip and fflate failed:", fallbackErr);
      throw new Error("Failed to extract ZIP file");
    }
  }

  if (entries.length === 0) {
    return { files: [], folderPaths: [] };
  }

  // Detect the common wrapper prefix (top-level folder in the ZIP)
  const allPaths = entries.map((e) => e.path);
  const firstSegments = new Set(allPaths.map((p) => p.split("/")[0]));
  const hasWrapper = firstSegments.size === 1 && allPaths.every((p) => p.includes("/"));
  const wrapperPrefix = hasWrapper ? allPaths[0].split("/")[0] + "/" : "";

  console.log(`   Wrapper: ${wrapperPrefix || "(none)"}`);

  for (const { path, blob } of entries) {
    // Strip wrapper prefix
    const relPath = wrapperPrefix ? path.substring(wrapperPrefix.length) : path;
    if (!relPath) continue;

    const segs = relPath.split("/").filter(Boolean);
    const fileName = segs[segs.length - 1];
    const folderPath = segs.slice(0, -1).join("/");

    // Record folder paths (including all ancestors)
    if (folderPath) {
      const pathSegs = folderPath.split("/");
      for (let i = 1; i <= pathSegs.length; i++) {
        folderPaths.add(pathSegs.slice(0, i).join("/"));
      }
    }

    const file = new File([blob], fileName, { type: blob.type || "" });
    (file as any).__mirrorPath = relPath;
    files.push(file);
  }

  // Also add directory entries from the ZIP (for empty folders)
  // JSZip marks them with entry.dir, fflate marks them with trailing "/"
  // We already collected folder paths from file paths above, but let's
  // also check raw ZIP entries for dirs
  try {
    const zip = await JSZip.loadAsync(zipFile);
    for (const [path, entry] of Object.entries(zip.files)) {
      if (!entry.dir) continue;
      const relPath = wrapperPrefix ? path.substring(wrapperPrefix.length) : path;
      const clean = relPath.replace(/\/$/, "");
      if (clean) folderPaths.add(clean);
    }
  } catch {
    // Already extracted via fflate — dir entries captured from file paths
  }

  const folderArr = Array.from(folderPaths).sort();
  console.log(`📦 ZIP scan: ${files.length} files, ${folderArr.length} folders`);
  if (folderArr.length > 0) console.log(`   Folders:`, folderArr.slice(0, 20));

  return { files, folderPaths: folderArr };
}

// ---------- Diff types ----------

export type MirrorFile = {
  file: File;
  relativePath: string; // e.g., "ABC Corp/Financials/Q4.pdf"
  folderPath: string;   // e.g., "ABC Corp/Financials"
  fileName: string;     // e.g., "Q4.pdf"
};

export type MirrorDiff = {
  newFolders: string[];
  newFiles: MirrorFile[];
  skippedFiles: MirrorFile[];
  excludedFileCount: number;              // files dropped because they live under an excluded folder
  existingFolderMap: Map<string, string>; // lowercase path → SP itemId
  copyCounts: Map<string, number>;        // lowercase file name → how many folders it sits in (new + synced)
};

// ---------- Excluded folders ----------

/**
 * True if `path` is one of the excluded folders or lives under one.
 * `excluded` holds lowercase relative folder paths.
 */
export function isUnderExcluded(path: string, excluded: Set<string>): boolean {
  if (excluded.size === 0) return false;
  const segs = path.toLowerCase().split("/").filter(Boolean);
  for (let i = 1; i <= segs.length; i++) {
    if (excluded.has(segs.slice(0, i).join("/"))) return true;
  }
  return false;
}

// ---------- Ignored file patterns ----------

const IGNORE_PATTERNS = [
  /^~\$/, /\.tmp$/i, /\.crdownload$/i, /\.partial$/i,
  /^\./, /\bthumbs\.db$/i, /\bdesktop\.ini$/i, /\.ds_store$/i,
];

function shouldIgnore(fileName: string): boolean {
  return IGNORE_PATTERNS.some((p) => p.test(fileName));
}

// ---------- Diff computation ----------

/**
 * Compare a local scan against a SharePoint tree.
 * Anything at or under a path in `excludedFolders` (lowercase) is left out entirely.
 */
export function computeMirrorDiff(
  scan: LocalScan,
  spTree: SPTreeNode,
  excludedFolders: Set<string> = new Set()
): MirrorDiff {
  const spFolderMap = flattenTreeByPath(spTree);    // stores both cases
  const spFilePaths = flattenTreeFilePaths(spTree); // lowercase full paths

  console.log(`🔍 computeMirrorDiff: ${scan.files.length} local files, ${scan.folderPaths.length} local folders, ${excludedFolders.size} excluded`);
  console.log(`   SP has ${spFolderMap.size / 2} folders, ${spFilePaths.size} files`);

  const newFolders = new Set<string>();
  const newFiles: MirrorFile[] = [];
  const skippedFiles: MirrorFile[] = [];
  let excludedFileCount = 0;

  // 1. Every local folder that isn't in SP = new folder.
  // Also expand ancestors — if an empty intermediate folder has no files,
  // it won't be caught by the file loop below, so we must ensure its parents
  // are added here too, otherwise creation will fail with "parent missing".
  for (const fp of scan.folderPaths) {
    if (isUnderExcluded(fp, excludedFolders)) continue;
    if (!spFolderMap.has(fp.toLowerCase())) {
      newFolders.add(fp);
      const pathSegs = fp.split("/");
      for (let i = 1; i < pathSegs.length; i++) {
        const anc = pathSegs.slice(0, i).join("/");
        if (!spFolderMap.has(anc.toLowerCase())) {
          newFolders.add(anc);
        }
      }
    }
  }

  // 2. For each file, decide new vs skipped
  for (const file of scan.files) {
    const relPath = (file as any).__mirrorPath as string;
    if (!relPath) continue;

    const segs = relPath.split("/").filter(Boolean);
    const fileName = segs[segs.length - 1];
    const folderPath = segs.slice(0, -1).join("/");

    if (folderPath && isUnderExcluded(folderPath, excludedFolders)) {
      excludedFileCount++;
      continue;
    }

    if (shouldIgnore(fileName)) continue;

    const mf: MirrorFile = {
      file,
      relativePath: relPath,
      folderPath,
      fileName,
    };

    const fullLower = relPath.toLowerCase();
    if (spFilePaths.has(fullLower)) {
      skippedFiles.push(mf);
      continue;
    }

    // Any ancestor folder that's missing from SP should be created.
    // (Already collected from scan.folderPaths above, but this covers
    // folders that only exist because a file lives in them — input picker case.)
    if (folderPath) {
      const pathSegs = folderPath.split("/");
      for (let i = 1; i <= pathSegs.length; i++) {
        const anc = pathSegs.slice(0, i).join("/");
        if (!spFolderMap.has(anc.toLowerCase())) {
          newFolders.add(anc);
        }
      }
    }

    newFiles.push(mf);
  }

  // Some VDRs file the same document under several folders. Each copy is its own
  // upload to its own folder (and is skipped if already there) — count them so the
  // preview can say so.
  const copyCounts = new Map<string, number>();
  for (const mf of [...newFiles, ...skippedFiles]) {
    const key = mf.fileName.toLowerCase();
    copyCounts.set(key, (copyCounts.get(key) ?? 0) + 1);
  }

  const newFoldersArr = Array.from(newFolders).sort(
    (a, b) => a.split("/").length - b.split("/").length || a.localeCompare(b)
  );

  console.log(`🔍 Diff: ${newFoldersArr.length} new folders, ${newFiles.length} new files, ${skippedFiles.length} skipped, ${excludedFileCount} excluded`);
  if (newFoldersArr.length > 0) console.log(`   New folders:`, newFoldersArr);

  return {
    newFolders: newFoldersArr,
    newFiles,
    skippedFiles,
    excludedFileCount,
    existingFolderMap: spFolderMap,
    copyCounts,
  };
}
